import { clearAllChatCache } from "services/chatMessageCache";

export type AuthGuard = "admin" | "instructor";

const GUARD_KEY = "auth_guard";
const ADMIN_TOKEN_KEY = "admin_auth_token";
const INSTRUCTOR_TOKEN_KEY = "instructor_auth_token";

export const authStorage = {
  getGuard(): AuthGuard {
    const raw = localStorage.getItem(GUARD_KEY);
    return raw === "instructor" ? "instructor" : "admin";
  },

  setGuard(guard: AuthGuard) {
    localStorage.setItem(GUARD_KEY, guard);
  },

  guardFromPath(pathname: string): AuthGuard | null {
    const path = pathname || "";
    if (path.startsWith("/instructor") || path.includes("/auth/instructor")) {
      return "instructor";
    }
    if (
      path.startsWith("/admin") ||
      path.startsWith("/rtl") ||
      path.includes("/auth/admin")
    ) {
      return "admin";
    }
    return null;
  },

  getToken(guard: AuthGuard): string | null {
    return localStorage.getItem(guard === "admin" ? ADMIN_TOKEN_KEY : INSTRUCTOR_TOKEN_KEY);
  },

  setToken(guard: AuthGuard, token: string) {
    localStorage.setItem(guard === "admin" ? ADMIN_TOKEN_KEY : INSTRUCTOR_TOKEN_KEY, token);
    this.setGuard(guard);
  },

  clearToken(guard: AuthGuard) {
    localStorage.removeItem(guard === "admin" ? ADMIN_TOKEN_KEY : INSTRUCTOR_TOKEN_KEY);
    const otherGuard: AuthGuard = guard === "admin" ? "instructor" : "admin";
    if (this.getToken(otherGuard)) {
      this.setGuard(otherGuard);
    } else {
      localStorage.removeItem(GUARD_KEY);
    }
    // Drop locally cached chat messages on logout.
    clearAllChatCache();
  },

  clearAll() {
    localStorage.removeItem(GUARD_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(INSTRUCTOR_TOKEN_KEY);
    clearAllChatCache();
  },

  getTokenForPath(pathname?: string): string | null {
    const path = pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
    if (path.startsWith("/auth/")) {
      return null;
    }
    const guard = this.guardFromPath(path) ?? this.getGuard();
    return this.getToken(guard);
  },

  getActiveToken(): string | null {
    return this.getTokenForPath();
  },

  isAuthenticated(guard: AuthGuard): boolean {
    return !!this.getToken(guard);
  },

  loginPath(guard: AuthGuard) {
    return guard === "admin" ? "/auth/admin-sign-in" : "/auth/instructor-sign-in";
  },

  dashboardPath(guard: AuthGuard) {
    return guard === "admin" ? "/admin/default" : "/instructor/default";
  },
};
